<template>
    <div class="payment__form">
        <form
            v-if="state.currentView == 'USSD_INIT_VIEW' && state.processing != true"
            @submit.prevent="checkBank"
        >
            <p class="container__col-md-12 container__col-sm-12 payment__instruction">
                {{$t("ussd.init.instruction")}}
            </p>
            <primary-select
                :title="$t('ussd.select.bank')"
                :description-text="$t('ussd.select.description')"
                @value-change="bank = $event"
                :options="state.banks"
                :defaultValue="
                    getValueFromPath(state, 'inputs.bank.value') || null
                "
                inputName="bank"
            />
            <PrimaryButton
                :content="
                    [$t('ui.pay.btn'), state.currency, state.amount.toLocaleString()]
                        .filter(Boolean)
                        .join(' ')
                "
                :disabled="bank == '' || bank == null ? true : false"
                type="submit"
            />
        </form>
        <template
            v-else-if="
                state.currentView == 'USSD_CODE_VIEW' &&
                    state.processing != true &&
                    !showProcessing
            "
        >
            <p class="container__col-md-12 container__col-sm-12 payment__instruction">
                {{$t("ussd.pay.instruction.a")}} <strong>{{ state.context.selectedBank.name }}</strong> {{$t("ussd.pay.instruction.b")}}
            </p>
            <code
                class="container__col-md-12 container__col-sm-12 payment__ussd-code"
            >
                <a :href="`tel://${state.context.ussdCode}`" rel="nofollow">{{
                    state.context.ussdCode
                }}</a>
            </code>
            <div class="payment__ussd-copy">
                <button
                    @click="copyToClipboard(state.context.ussdCode)"
                    class="payment__ussd-btn"
                    type="button"
                >
                    <svg
                        width="10"
                        height="10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g clip-path="url(#clip0)" fill="#828282">
                            <path
                                d="M7.318 2.5H1.91a.955.955 0 00-.954.955v5.409c0 .527.427.954.954.954h5.41a.955.955 0 00.954-.954v-5.41a.955.955 0 00-.955-.954z"
                            />
                            <path
                                d="M8.91.91H3.181a.955.955 0 00-.955.946v.008h5.091c.878 0 1.59.712 1.591 1.59v5.092h.007a.955.955 0 00.948-.955V1.864a.955.955 0 00-.955-.955z"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0">
                                <path
                                    fill="#fff"
                                    transform="translate(.955 .91)"
                                    d="M0 0h8.909v8.909H0z"
                                />
                            </clipPath>
                        </defs>
                    </svg>
                    <span>{{$t("ussd.copy.instruction")}}</span>
                </button>
                <span v-if="showCopied" id="copy-message" class="copy-message"
                >{{$t("ussd.copy.text")}}</span
                >
            </div>
            <h6
                role="timer"
                v-html="timer"
                class="container__col-md-12 container__col-sm-12 payment__instruction--applepay timer"
            ></h6>
            <PrimaryButton
                @click="showProcessing = true, nextStep"
                :content="$t('ussd.complete.btn')"
            />
        </template>
        <template
            v-else-if="
                state.currentView == 'USSD_CODE_VIEW' &&
                    state.processing != true &&
                    showProcessing
            "
        >
            <payment-loading />
        </template>
    </div>
</template>

<script>
import { 
    ref, 
    watch,
    computed, 
    onBeforeUnmount,
} from "vue";
import { injectStore, mapState } from "redux-vuex";
import { toClipboard } from "@soerenmartius/vue3-clipboard";
import { getValueFromPath } from "@/support/helpers";
import PrimarySelect from "@/components/form/base-select";
import PrimaryButton from "@/components/form/base-button";
import PaymentLoading from "@/components/payment-loading.vue";
export default {
    name: "UssdPayment",
    components: {
        PaymentLoading,
        PrimaryButton,
        PrimarySelect,
    },
    setup() {
        const store = injectStore();
        const bank = ref("");
        const checkoutData = ref(store.getState());
        const transactionSuccessful = ref(false);
        const showProcessing = ref(false);
        const showCopied = ref(false);
        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });

        const timer = computed(() => {
            let time = "";
            if (checkoutData.value.view.context.timerCurrent) {
                let duration = checkoutData.value.view.context.timerCurrent.time;
                let minutes = Math.floor(duration / 60);
                let seconds = duration % 60;

                if (minutes > 0 && minutes != 1) {
                    time = `This code will expire after ${minutes}mins ${seconds}secs and can only be used for this transaction.`;
                }
                else if (minutes == 1) {
                    time = `This code will expire after ${minutes}mins ${seconds}secs and can only be used for this transaction.`;
                } 
                else {
                    time = `This code will expire after ${seconds}secs and can only be used for this transaction.`;
                }
            }
            return time;
        });

        function checkBank() {
            if (bank.value != "") {
                checkoutData.value.view.context.$actions.update(bank.value.code);
                nextStep();
            }
        }

        function nextStep() {
            setTimeout(() => checkoutData.value.view.context.$actions.next(), 0);       
        }
        function copyToClipboard(data) {
            toClipboard(data);
            showCopied.value = true;
            setTimeout(() => {
                showCopied.value = false;
            }, 700);
        }
        watch(checkoutData, (val) => {
            if ( val.view != undefined &&
                val.view.current != "USSD_CODE_VIEW" &&
        checkoutData.value.processing != true
            ) {
                showProcessing.value = false;
            }
        });
        onBeforeUnmount(() => {
            subscription();
        });
        return {
            state: mapState({
                currency: (state) =>
                    getValueFromPath(state, "currency.current") || null,
                amount: (state) => getValueFromPath(state, "amount") || null,
                processing: (state) => getValueFromPath(state, "processing") || false,
                banks: (state) =>
                    getValueFromPath(state, "view.context.inputs.bank.options") || [],
                context: (state) => getValueFromPath(state, "view.context") || {},
                inputs: (state) => getValueFromPath(state, "view.context.inputs") || {},
                currentView: (state) => getValueFromPath(state, "view.current") || null,
            }),
            checkoutData,
            bank,
            getValueFromPath,
            transactionSuccessful,
            showProcessing,
            nextStep,
            timer,
            checkBank,
            showCopied,
            copyToClipboard,
        };
    },
    methods: {},
};
</script>

<style scoped>
.timer {
  text-align: center;
}
</style>